import { useModalHook } from '@/components/commons/modals/useModalHook';
import { useAddressSearchBuddyData } from '@/components/hooks/customs/messageSend/useAddressSearchBuddyData';
import { formatPhoneNumber, TelHyphen } from '@/shared/util/format/phoneNumberFormatUtil';
import { validateNumberLength, validatePhoneNumber } from '@/shared/util/validation/phoneNumber.validation';
import { Dropdown, Menu, Spin } from 'antd';
import React, { useEffect, useRef, useState } from 'react';

interface ISearchReceiverList {
  etcInputValueState: string; // 입력력
  setEtcInputValueState: React.Dispatch<React.SetStateAction<string>>;
  addEtcList: (e?: any, number?: string) => Promise<void>;
  searchRef: React.MutableRefObject<HTMLDivElement | null>;
}

const SearchReceiverList = (props: ISearchReceiverList) => {
  const [debounceKeyword, setDebounceKeyword] = useState<string>(''); // api 호출할 전화번호

  const [selectedIndex, setSelectedIndex] = useState<number>(0); // 키보드로 컨트롤할 아이템 index

  const [selectToggle, setSelectToggle] = useState<boolean>(false); // 검색창 토글할 STATE

  const searchRef = useRef<HTMLDivElement>(null); // 위치 조정을 위한 ref
  const menuRef = useRef<HTMLDivElement>(null); // 메뉴의 스크롤을 제어할 ref

  const useSearchBuddyListHooks = useAddressSearchBuddyData('', debounceKeyword); // 주소록 검색 훅

  // 리스트로 수신인 입력 성공
  const onClickBuddyListItem = (item: string) => {
    setDebounceKeyword('');
    props.addEtcList(null, item);
  };

  // 리스트로 수신인 입력 실패
  const onClickNoDataItem = () => {};

  // Debounce effect 주소록 검색
  useEffect(() => {
    setSelectToggle(true);
    const handler = setTimeout(() => {
      setDebounceKeyword(props.etcInputValueState.replace(/-/g, ''));
    }, 400);
    return () => {
      setSelectedIndex(0); // 셀렉트 아이템 첫번째 요소로 초기화
      clearTimeout(handler);
    };
  }, [props.etcInputValueState]);

  // Scroll handler to fetch more data
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      if (useSearchBuddyListHooks.hasNextPage && !useSearchBuddyListHooks.isFetchingNextPage) {
        useSearchBuddyListHooks.fetchNextPage();
      }
    }
  };

  // 키보드로 검색된 목록 조작하는 핸들러
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      setSelectToggle(false);
      return;
    }
    if (
      useSearchBuddyListHooks.isLoading ||
      (useSearchBuddyListHooks.buddyListData.length < 1 && !props.etcInputValueState)
    ) {
      setSelectedIndex(0);
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < useSearchBuddyListHooks.buddyListData.length - 1
          ? prev + 1
          : useSearchBuddyListHooks.buddyListData.length - 1,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter' && selectedIndex >= 0) {
      e.preventDefault();
      if (debounceKeyword === '' || props.etcInputValueState !== debounceKeyword) return;
      if (useSearchBuddyListHooks.buddyListData.length > 0) {
        onClickBuddyListItem(useSearchBuddyListHooks.buddyListData[selectedIndex].keyCommNo);
      } else if (validatePhoneNumber(props.etcInputValueState)) {
        // 여기서 주소록에 데이터는 없지만 강제로 추가하려고하는 값이 벨리데이션 여기서 검증하면됌
        if (validateNumberLength(props.etcInputValueState)) {
          onClickBuddyListItem(props.etcInputValueState);
        } else {
          onClickNoDataItem();
        }
      } else {
        onClickNoDataItem();
      }
    }
  };

  // 키보드 이벤트 감지할 useEffect
  useEffect(() => {
    const handleKeyEvent = (e: KeyboardEvent) => handleKeyDown(e as any);

    if (props.searchRef.current) {
      props.searchRef.current.addEventListener('keydown', handleKeyEvent);
    }

    return () => {
      if (props.searchRef.current) {
        props.searchRef.current.removeEventListener('keydown', handleKeyEvent);
      }
    };
  }, [selectedIndex, useSearchBuddyListHooks.buddyListData, selectToggle]);

  // 선택된 아이템 view 따라갈 useEffect
  useEffect(() => {
    if (menuRef.current) {
      const menuItems = menuRef.current.querySelectorAll('.ant-menu-item');
      const selectedItem = menuItems[selectedIndex];

      if (selectedItem) {
        // 스크롤을 해당 아이템으로 맞추기
        selectedItem.scrollIntoView({
          behavior: 'auto',
          block: 'nearest', // 'nearest'로 설정하여 항목이 보이지 않으면 가장 가까운 위치로 스크롤
        });
      }
    }
  }, [selectedIndex]);

  const menuItems =
    useSearchBuddyListHooks.buddyListData.length > 0
      ? useSearchBuddyListHooks.buddyListData.map((buddy, index) => ({
          key: buddy.buddySeqNo,
          label: `${buddy.buddyNm}, (${TelHyphen(buddy.keyCommNo)})`,
          onClick: () => onClickBuddyListItem(buddy.keyCommNo),
          style: selectedIndex === index ? { backgroundColor: '#1890ff', color: 'white' } : {},
        }))
      : [
          {
            key: props.etcInputValueState,
            label: validatePhoneNumber(props.etcInputValueState)
              ? `'${formatPhoneNumber(props.etcInputValueState)}' 수신번호 추가하기`
              : `'${props.etcInputValueState}' 검색 결과 없음`,
            onClick: () =>
              validatePhoneNumber(props.etcInputValueState)
                ? onClickBuddyListItem(props.etcInputValueState)
                : onClickNoDataItem(),
            style: selectedIndex === 0 ? { backgroundColor: '#1890ff', color: 'white' } : {},
          },
        ];

  return (
    <div style={{ position: 'relative' }} ref={searchRef}>
      {selectToggle && !useSearchBuddyListHooks.isLoading && debounceKeyword && (
        <div
          role="listbox"
          tabIndex={0} // 포커스 가능하도록 설정
          onKeyDown={handleKeyDown}
          onScroll={handleScroll}
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            maxHeight: 200,
            overflowY: 'auto',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
          }}
          ref={menuRef}
        >
          <Menu items={menuItems} selectable={false} itemType="small" />
          {useSearchBuddyListHooks.isLoading && (
            <div style={{ textAlign: 'center', padding: 8 }}>
              <Spin size="small" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
export default SearchReceiverList;
